import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Alert, Box, Card, CardContent, Chip, LinearProgress, MenuItem, TextField, Typography } from '@mui/material';
import { answerLabel, schoolApi } from '../../common/schoolApi';
import { QuestionView } from '../school-test/QuestionView';

/**
 * Question by question (GET /assignment/analysis/5), over the counting
 * attempts of the students in the caller's sections who sat it.
 *
 * Two flags, raised by the API only once enough students answered
 * (`min_sample`): `hard` -- under 30% got it right; `check_key` -- one wrong
 * answer was chosen more often than the key, which is usually a wrong key or
 * a badly worded question.
 */

const FLAG: Record<string, { label: string; help: string }> = {
    hard: { label: 'Hard', help: 'Fewer than 30% got this right.' },
    check_key: { label: 'Check the key', help: 'A wrong answer was chosen more often than the key. The key or the wording may be wrong.' },
};

export const AnalysisTab = ({ assignment }: { assignment: any }) => {
    const notify = useNotify();
    const [data, setData] = useState<any>(null);
    const [section, setSection] = useState<number | ''>('');

    useEffect(() => {
        schoolApi
            .get(`assignment/analysis/${assignment.id}`, { class_section_id: section })
            .then(r => setData(r.result))
            .catch(e => notify(e.message, { type: 'error' }));
    }, [assignment.id, section]);

    if (!data) return <Typography>Loading…</Typography>;

    const flagged = data.questions.filter((q: any) => q.flags.length > 0).length;
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                {(assignment.sections || []).length > 1 && (
                    <TextField select size="small" label="Section" value={section} onChange={e => setSection(e.target.value as any)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All my sections</MenuItem>
                        {assignment.sections.map((sec: any) => (
                            <MenuItem key={sec.id} value={sec.id}>
                                {sec.label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <Typography variant="body2" color="text.secondary">
                    Over {data.sat} student{data.sat === 1 ? '' : 's'} who sat it.
                    {data.sat < data.min_sample && ` Questions are flagged once at least ${data.min_sample} have answered them.`}
                    {flagged > 0 && ` ${flagged} question${flagged === 1 ? '' : 's'} flagged.`}
                </Typography>
            </Box>

            {data.questions.length === 0 && <Alert severity="info">Nobody has submitted yet.</Alert>}

            {data.questions.map((q: any) => (
                <Card key={`${q.question_source}:${q.question_id}`} variant="outlined" sx={{ mb: 2, borderColor: q.flags.length ? 'warning.main' : undefined }}>
                    <CardContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Q{q.question_order} · {q.marks} marks{q.negative_marks ? `, ${Math.abs(q.negative_marks)} off if wrong` : ''}
                            </Typography>
                            <QuestionView q={q.content} answer={q.correct_answer} compact />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                {q.flags.map((f: string) => (
                                    <Chip key={f} size="small" color="warning" label={FLAG[f]?.label || f} title={FLAG[f]?.help} />
                                ))}
                            </Box>
                            <Typography variant="body2">
                                <b>{q.correct_pct}%</b> right
                            </Typography>
                            <LinearProgress variant="determinate" value={Math.min(100, q.correct_pct)} color="success" sx={{ height: 8, borderRadius: 1, mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {q.correct} right · {q.incorrect} wrong · {q.unanswered} blank · avg {q.avg_time}s
                            </Typography>
                            {q.answers.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Answers given
                                    </Typography>
                                    {q.answers.map((ans: any) => (
                                        <Box key={ans.answer} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ width: 60, fontWeight: ans.is_key ? 600 : 400, color: ans.is_key ? 'success.main' : 'inherit' }}>
                                                {answerLabel(ans.answer, q.question_type)}
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(ans.count / Math.max(1, q.answered)) * 100}
                                                color={ans.is_key ? 'success' : 'error'}
                                                sx={{ flex: 1, height: 6, borderRadius: 1 }}
                                            />
                                            <Typography variant="caption" sx={{ width: 30, textAlign: 'right' }}>
                                                {ans.count}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};
